import { createSelector, MemoizedSelector } from '@ngrx/store';

import { Category, FinnairLowestPrice, FinnairServiceVariantItemV3, SubCategory } from '@fcom/dapi/api/models';
import { mapValues } from '@fcom/core/utils';
import { ServiceSelections, TravelerService } from '@fcom/dapi/interfaces';

import { getLowestPrice } from '../../utils/common-booking.utils';
import { SEAT_BUNDLE_CATEGORIES } from '../../modules/ancillaries/utils';
import { selectCommonBookingState } from './feature.selector';
import { CommonBookingFeatureState, CommonBookingState } from '../store.interface';

/**
 * Returns all available seat bundles for all the flights in the seatmap,
 * filtered to only include categories that are eligible to sell according to cart/order eligibilities.
 */
export const seatBundleCatalogByFlightSelector = createSelector(
  selectCommonBookingState,
  (state: CommonBookingState) => {
    const eligibilities = state?.services?.bundleEligibilities;
    return mapValues(state?.seatMap?.seatmaps || {}, (flight, flightId) => {
      const items = flight?.seatMap?.bundleServiceCatalogItems || [];
      if (!eligibilities?.categories?.length) {
        return items;
      }
      return items.filter((item) => {
        const categoryEligibilities = eligibilities.categories.filter((eligCat) => eligCat.category === item.category);
        if (!categoryEligibilities.length) {
          return true;
        }
        return categoryEligibilities.some((eligCat) =>
          Object.values(eligCat.services[flightId] || {}).some((paxElig) => paxElig.isAllowedToUse)
        );
      });
    });
  }
);

/**
 * Allows finding out which subcategories belong to a bundle category.
 * Include all categories that appear under seat maps for any of the flights on the journey.
 */
export const seatBundleCategoriesToSubCategoriesMappingSelector = createSelector(
  selectCommonBookingState,
  (state: CommonBookingState) => {
    const catalogItems = Object.values(state?.seatMap?.seatmaps || {}).flatMap(
      (f) => f?.seatMap?.bundleServiceCatalogItems || []
    );
    return catalogItems?.reduce((acc: Partial<Record<Category, Set<SubCategory>>>, cur) => {
      const curSubCategories = Object.values(cur.services || {})
        .flatMap((byFlight) => Object.values(byFlight))
        .flat(1)
        .flatMap((s) => s.subCategory);
      return {
        ...acc,
        [cur.category]: new Set<SubCategory>([...(acc[cur.category] || []), ...curSubCategories]),
      };
    }, {});
  }
);

/**
 * Allows finding out which category a subcategory belongs to.
 * Include all categories that appear under seat maps for any of the flights on the journey.
 */
export const seatBundleSubCategoriesToCategoriesMappingSelector = createSelector(
  seatBundleCategoriesToSubCategoriesMappingSelector,
  (catsToSubcats) =>
    Object.fromEntries(
      Object.entries(catsToSubcats).flatMap(([cat, subCats]) => Array.from(subCats).map((sc) => [sc, cat]))
    )
);

/**
 * Allows determining if a cateogry is a seat bundle or iterating through seat bundle category types.
 */
export const seatBundleCategoriesSelector = createSelector(
  seatBundleCategoriesToSubCategoriesMappingSelector,
  (catsToSubCats) => new Set(Object.keys(catsToSubCats) as Category[])
);

/**
 * Allows determining if a subcategory is a seat bundle or iterating through seat bundle category types.
 */
export const seatBundleSubCategoriesSelector: MemoizedSelector<CommonBookingState, Set<SubCategory>> = createSelector(
  seatBundleSubCategoriesToCategoriesMappingSelector,
  (subCatsToCats) => new Set(Object.keys(subCatsToCats) as SubCategory[])
);

/**
 * Returns seat bundle catalog service variant items for all flights and passengers for which there are any offered.
 */
export const seatBundleCatalogServicesByFlightAndPassengerSelector = createSelector(
  seatBundleCatalogByFlightSelector,
  (catalogByFlight): Record<string, Record<string, FinnairServiceVariantItemV3[]>> =>
    Object.entries(catalogByFlight).reduce((acc, [flightId, items]) => {
      const allRelevantPaxes = Array.from(new Set(items.flatMap((i) => Object.keys(i.services[flightId]))));
      const byPax = allRelevantPaxes.reduce((byPaxAcc, paxId) => {
        const allItemsForPaxForFlight = items.flatMap((i) => i.services?.[flightId]?.[paxId] || []);
        return {
          ...byPaxAcc,
          [paxId]: allItemsForPaxForFlight,
        };
      }, {});
      return {
        ...acc,
        [flightId]: byPax,
      };
    }, {})
);

/**
 * Returns all available services for a flight+passenger+category.
 */
export const bundleCatalogServicesForFlightAndPassengerInCategorySelector = (props: {
  flightId: string;
  travelerId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, FinnairServiceVariantItemV3[]> =>
  createSelector(
    seatBundleCatalogServicesByFlightAndPassengerSelector,
    seatBundleSubCategoriesToCategoriesMappingSelector,
    (seatBundlesByFlightAndPax, subCategoryMapping) =>
      seatBundlesByFlightAndPax[props.flightId]?.[props.travelerId]?.filter(
        (s) => subCategoryMapping[s.subCategory] === props.category
      ) || []
  );

/**
 * Returns whether the catalog offers a bundle that matches flight+passenger+category.
 */
export const seatBundleCatalogContainsMatchSelector = (props: {
  flightId: string;
  travelerId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, boolean> =>
  createSelector(
    bundleCatalogServicesForFlightAndPassengerInCategorySelector(props),
    (matchingServices) => matchingServices.length > 0
  );

/**
 * Returns subset of services selections that matches any of the seat bundle categories.
 * Maps missing selections to empty objects to reduce need for undefined checking everywhere.
 */
export const seatBundleSelectionsSelector = createSelector(
  selectCommonBookingState,
  (state): Record<string, Partial<ServiceSelections>> =>
    SEAT_BUNDLE_CATEGORIES.reduce(
      (selections, category) => ({
        ...selections,
        [category]: state?.services?.selections?.[category as keyof ServiceSelections] || {},
      }),
      {}
    )
);

/**
 * Returns mapping of flights to passengers to seat bundle services they have selected
 */
export const seatBundleSelectionsByFlightAndPassengerSelector = createSelector(
  seatBundleSelectionsSelector,
  (allSeatBundleSelections): Record<string, Record<string, TravelerService[]>> => {
    const flightIds = new Set(Object.values(allSeatBundleSelections).flatMap((s) => Object.keys(s)));
    const paxIds = new Set(
      Object.values(allSeatBundleSelections).flatMap((s) => Object.values(s).flatMap((p) => Object.keys(p)))
    );
    return Array.from(flightIds).reduce(
      (byFlightAcc, curFlightId) => ({
        ...byFlightAcc,
        [curFlightId]: Array.from(paxIds).reduce(
          (byPaxAcc, curPaxId) => ({
            ...byPaxAcc,
            [curPaxId]: Object.values(allSeatBundleSelections).flatMap((s) => s[curFlightId]?.[curPaxId] || []),
          }),
          {}
        ),
      }),
      {}
    );
  }
);

/**
 * Returns whether the passenger has an active bundle of a specific category for the given flight.
 */
export const passengerHasSpecificBundleSelector = (props: {
  flightId: string;
  travelerId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, boolean | undefined> =>
  createSelector(
    seatBundleSelectionsByFlightAndPassengerSelector,
    seatBundleSubCategoriesToCategoriesMappingSelector,
    (selections, subCategoryMapping): boolean | undefined =>
      selections[props.flightId]?.[props.travelerId]?.some(
        (s: TravelerService) => subCategoryMapping[s.category] === props.category
      )
  );

/**
 * Returns the minimum quota value across all service variants in a given bundle category for a flight.
 * Returns undefined if no quota is set on any of the variants.
 */
export const bundleQuotaForFlightAndCategorySelector = (props: {
  flightId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, number | undefined> =>
  createSelector(
    seatBundleCatalogServicesByFlightAndPassengerSelector,
    seatBundleCategoriesToSubCategoriesMappingSelector,
    (catalogByFlight, categoryMapping): number | undefined => {
      const subCatsForCategory = categoryMapping[props.category];
      if (!subCatsForCategory) {
        return undefined;
      }
      const quotas = Object.values(catalogByFlight[props.flightId] || {})
        .flat()
        .filter((serviceVariant) => subCatsForCategory.has(serviceVariant.subCategory))
        .map((serviceVariant) => serviceVariant.quota)
        .filter((quotaValue): quotaValue is number => quotaValue !== undefined && quotaValue !== null);
      return quotas.length ? Math.min(...quotas) : undefined;
    }
  );

/**
 * Returns how many passengers have already selected a bundle of the given category for the given flight.
 */
export const bundleSelectionsCountForFlightAndCategorySelector = (props: {
  flightId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, number> =>
  createSelector(
    seatBundleSelectionsByFlightAndPassengerSelector,
    seatBundleSubCategoriesToCategoriesMappingSelector,
    (selectionsByFlightAndPax, subCategoryMapping): number => {
      const passengerSelections = selectionsByFlightAndPax[props.flightId] || {};
      return Object.values(passengerSelections).filter((paxServices) =>
        paxServices.some((service) => subCategoryMapping[service.category] === props.category)
      ).length;
    }
  );

/**
 * Returns true when the available quota for a bundle category on a flight has been fully consumed by
 * the current passenger selections (i.e., no more passengers can add this bundle).
 */
export const bundleQuotaReachedForFlightAndCategorySelector = (props: {
  flightId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, boolean> =>
  createSelector(
    bundleQuotaForFlightAndCategorySelector(props),
    bundleSelectionsCountForFlightAndCategorySelector(props),
    (quota, selectedCount): boolean => quota !== undefined && selectedCount >= quota
  );

/**
 * Returns the lowest price for a specific bundle category and flight from the seat bundle catalog.
 * Returns null if no catalog item exists for the given category/flight or if no lowestPrice is set.
 */
export const seatBundleLowestPriceForFlightAndCategorySelector = (
  flightId: string,
  category: Category
): MemoizedSelector<CommonBookingFeatureState, FinnairLowestPrice | null> =>
  createSelector(seatBundleCatalogByFlightSelector, (catalogByFlight): FinnairLowestPrice | null => {
    const catalogForFlight = catalogByFlight[flightId] || [];
    const catalogForCategory = catalogForFlight.find((item) => item.category === category);
    if (!catalogForCategory?.lowestPrice) {
      return null;
    }
    return getLowestPrice(catalogForCategory.lowestPrice, [flightId]);
  });

/**
 * Returns whether the passenger should be able to add bundle to a flight (is in catalog, doesn't yet have conflicting bundle, and quota not exhausted).
 */
export const passengerCanAddBundleSelector = (props: {
  flightId: string;
  travelerId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, boolean> =>
  createSelector(
    seatBundleCatalogContainsMatchSelector(props),
    seatBundleSelectionsByFlightAndPassengerSelector,
    seatBundleSubCategoriesToCategoriesMappingSelector,
    bundleQuotaReachedForFlightAndCategorySelector({ flightId: props.flightId, category: props.category }),
    (availableInCatalog, selectedBundlesByFlightAndPax, subCategoryMapping, quotaReached) =>
      availableInCatalog &&
      !selectedBundlesByFlightAndPax[props.flightId]?.[props.travelerId]?.some(
        (service) => subCategoryMapping[service.category] === props.category
      ) &&
      !quotaReached
  );

/**
 * Returns a map of flights to passengers to seat bundle categories to seat numbers to the specific matching service.
 * E.g. returnedValue[flightId][paxId][Category.SEAT_WIFI]['7B'] will return the matching traveler service if any.
 */
export const bundleServiceMatchesForSeatSelector = createSelector(
  seatBundleCatalogServicesByFlightAndPassengerSelector,
  seatBundleSubCategoriesToCategoriesMappingSelector,
  (byFlight, subCatsToCats) =>
    mapValues(byFlight, (byPax) =>
      mapValues(byPax, (services) =>
        services.reduce((acc, cur) => {
          const category = subCatsToCats[cur.subCategory];
          return {
            ...acc,
            [category]: {
              ...(acc[category] || {}),
              ...(cur.parameters?.seatNumbers || []).reduce(
                (snAcc, snCur) => ({
                  ...snAcc,
                  [snCur]: cur,
                }),
                {}
              ),
            },
          };
        }, {})
      )
    )
);

/**
 * Returns passengers mapped to the top level bundle categories that they have active for a given flight.
 */
export const selectedBundleCategoriesByPassengerSelector = (props: {
  flightId: string;
}): MemoizedSelector<CommonBookingFeatureState, Record<string, Category[]>> =>
  createSelector(
    seatBundleCategoriesToSubCategoriesMappingSelector,
    seatBundleSelectionsByFlightAndPassengerSelector,
    (bundleCategoriesMapping, selectionsByFlightAndPax): Record<string, Category[]> => {
      const mappingEntries = Object.entries(bundleCategoriesMapping) as Array<[Category, Set<SubCategory>]>;
      return mapValues(selectionsByFlightAndPax[props.flightId] || {}, (bundleServicesSelected) =>
        bundleServicesSelected.map((s) => mappingEntries.find(([_cat, subCats]) => subCats.has(s.category))[0])
      );
    }
  );

/**
 * Derives the ancillary variant code for each SEAT_* bundle category by parsing the bundle variant
 * naming convention BDL_{seatType}_{variantCode} (e.g. "BDL_L_WIEF" → "WIEF").
 * Returns a map of bundle category → ancillary variant code (e.g. Category.SEAT_WIFI → "WIEF").
 */
export const bundleCategoryToAncillaryVariantSelector = createSelector(
  seatBundleCatalogByFlightSelector,
  (catalogByFlight): Partial<Record<Category, string>> =>
    Object.values(catalogByFlight)
      .flatMap((items) => items)
      .reduce(
        (acc, item) => {
          if (item.category in acc) return acc;
          const firstVariant = Object.values(item.services)
            .flatMap((byPax) => Object.values(byPax))
            .flat()[0]?.variant;
          if (firstVariant) {
            const ancillaryCode = firstVariant.split('_').pop();
            if (ancillaryCode) acc[item.category] = ancillaryCode;
          }
          return acc;
        },
        {} as Partial<Record<Category, string>>
      )
);

export const getBundleAncillaryVariantByCategorySelector = (
  category: Category
): MemoizedSelector<CommonBookingFeatureState, string | undefined> =>
  createSelector(bundleCategoryToAncillaryVariantSelector, (categoryToVariant) => categoryToVariant[category]);
